import { atom } from "xoid";
import { useAtom } from "@xoid/react";
import { featureFlags } from "@common/feature-flags";
import { env } from "./env";

type KioskFlags = typeof featureFlags.kiosk;

type RuntimeConfig = {
  kiosk?: Partial<KioskFlags> & { driverUrl?: string };
  driverUrl?: string;
  /** Backend API base URL; overrides VITE_REACT_APP_SERVER_URL when set (e.g. by Ansible). */
  serverUrl?: string;
  cashAgentUrl?: string;
  /** When set, this hosted kiosk always authenticates as this shop. */
  kioskKey?: string;
};

let configOverrides: Partial<KioskFlags> = {};
const runtimeOverrides = atom<Partial<KioskFlags>>({});
// Bumped whenever backend flags are applied, so useFeatureFlags() re-renders.
const backendFlagsVersion = atom(0);

let driverUrl = "https://localhost:8080";
let serverUrlOverride: string | undefined;
let cashAgentUrlOverride: string | undefined;
let kioskKeyOverride: string | undefined;

// featureFlags.kiosk is read on every resolve (not copied once at import) so
// backend overrides applied later via applyDynamicFeatureFlags are picked up.
function resolveFlags(): KioskFlags {
  return {
    ...featureFlags.kiosk,
    ...configOverrides,
    ...runtimeOverrides.value,
  };
}

const featureFlagsAtom = atom((get) => {
  get(backendFlagsVersion);
  return {
    ...featureFlags.kiosk,
    ...configOverrides,
    ...get(runtimeOverrides),
  } as KioskFlags;
});

/** Call after applying backend feature flags so subscribed components re-render. */
export function notifyBackendFlagsChanged(): void {
  backendFlagsVersion.update((version) => version + 1);
}

export async function loadFeatureFlags(): Promise<void> {
  try {
    const response = await fetch("/config.json");
    if (!response.ok) return;
    const config: RuntimeConfig = await response.json();
    if (config.kiosk) {
      configOverrides = { ...config.kiosk };
    }
    console.log("config", config);
    if (config.kiosk?.driverUrl) {
      driverUrl = config.kiosk.driverUrl;
    } else if (config.driverUrl) {
      driverUrl = config.driverUrl;
    }
    if (typeof config.serverUrl === "string" && config.serverUrl.length > 0) {
      serverUrlOverride = config.serverUrl.replace(/\/$/, "");
    }
    if (typeof config.cashAgentUrl === "string" && config.cashAgentUrl.length > 0) {
      cashAgentUrlOverride = config.cashAgentUrl.replace(/\/$/, "");
    }
    if (typeof config.kioskKey === "string" && config.kioskKey.length > 0) {
      kioskKeyOverride = config.kioskKey;
    }
  } catch {
    // config.json not found or invalid — use compiled defaults
  }
}

export function getFeatureFlags(): KioskFlags {
  const flags = resolveFlags();
  // @ts-ignore
  window.featureFlags = flags;
  return flags;
}

export function useFeatureFlags(): KioskFlags {
  return useAtom(featureFlagsAtom);
}

export function updateFeatureFlags(overrides: Partial<KioskFlags>): void {
  runtimeOverrides.set({ ...runtimeOverrides.value, ...overrides });
}

export function resetFeatureFlags(): void {
  runtimeOverrides.set({});
}

export function getDriverUrl(): string {
  return driverUrl;
}

/**
 * Backend API base URL.
 * Prefers runtime config.json `serverUrl` (per-kiosk Ansible deploy),
 * otherwise the Vite-baked VITE_REACT_APP_SERVER_URL.
 */
export function getServerUrl(): string {
  return serverUrlOverride ?? env.VITE_REACT_APP_SERVER_URL;
}

export function getCashAgentUrl(): string {
  return cashAgentUrlOverride ?? env.VITE_CASH_AGENT_URL;
}

export function getConfiguredKioskKey(): string | undefined {
  return kioskKeyOverride;
}
